import {
  faCalendarAlt,
  faMoneyBillWave,
  faCalendarPlus,
  faCrown,
  faIdCard,
  faClock,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import { createSubscription, updateSubscription } from '@redux/features/subscriptionsSlice';
import { createMembershipFees } from '@redux/features/paymentsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { getUkrainianPlural } from 'helpers/getUkrainianPlural';
import { scrollDate } from 'helpers/scrollDate';
import { formatDate } from 'helpers/formatDate';
import { DatePicker } from 'components/DatePicker';
import { Checkbox } from 'components/Checkbox';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import styles from './SubscriptionForm.scss';

export const SubscriptionForm = ({ clientID, subscription = null, isEdit = false, onClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    type: 'subscription',
    startDate: '',
    endDate: '',
    price: '',
    createPayment: true,
    paymentMethod: '',
    dueTime: '',
  });

  const [selectedMonths, setSelectedMonths] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && subscription) {
      setFormData({
        type: subscription.type,
        startDate: subscription.startDate,
        endDate: subscription.endDate,
        price: '',
        createPayment: false,
        paymentMethod: '',
        dueTime: '',
      });
    } else if (!isEdit) {
      const today = formatDate(new Date());
      const nextMonth = new Date();
      nextMonth.setTime(nextMonth.getTime() + 30 * 24 * 60 * 60 * 1000);
      const nextMonthStr = formatDate(nextMonth);

      setFormData((prev) => ({
        ...prev,
        startDate: today,
        endDate: nextMonthStr,
        dueTime: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString().slice(0, 16),
      }));
    }
  }, [isEdit, subscription]);

  useEffect(() => {
    if (errors.submit)
      dispatch(addNotification({ id: Math.floor(Math.random()), title: errors.submit, type: 'error' }));
  }, [errors.submit]);

  const subscriptionTypes = [
    { value: 'subscription', label: 'Абонемент', icon: faIdCard },
    { value: 'membership', label: 'Членство', icon: faStar },
  ];

  const availableMonths = useMemo(() => {
    const months = [];
    const now = new Date();

    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i);
      months.push({
        date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`,
        label: date.toLocaleDateString('uk-UA', { month: 'long', year: 'numeric' }),
        month: date.getMonth(),
        year: date.getFullYear(),
      });
    }

    return months;
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) newErrors.type = 'Оберіть тип пакету';

    if (formData.type === 'membership' && !isEdit) {
      if (selectedMonths.length === 0) {
        newErrors.months = 'Оберіть хоча б один місяць';
      }
      if (formData.createPayment) {
        if (!formData.price || isNaN(formData.price) || Number(formData.price) <= 0) {
          newErrors.price = 'Вкажіть коректну вартість';
        }
      }
    } else if (formData.type === 'subscription' || isEdit) {
      if (!formData.startDate) newErrors.startDate = 'Вкажіть дату початку';
      if (!formData.endDate) newErrors.endDate = 'Вкажіть дату закінчення';

      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        if (start >= end) {
          newErrors.endDate = 'Дата закінчення повинна бути пізніше початку';
        }
      }

      if (formData.createPayment) {
        if (!formData.price || isNaN(formData.price) || Number(formData.price) <= 0) {
          newErrors.price = 'Вкажіть коректну вартість';
        }
        if (!formData.dueTime) {
          newErrors.dueTime = 'Вкажіть термін оплати';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (formData.type === 'membership' && !isEdit) {
      const payload = {
        clientID: Number(clientID),
        months: selectedMonths,
        amount: Number(formData.price),
      };

      const resultAction = await dispatch(createMembershipFees(payload));

      if (createMembershipFees.fulfilled.match(resultAction)) {
        onClose();
      }
      return;
    }

    const requestData = {
      clientID: Number(clientID),
      type: formData.type,
      startDate: formData.startDate,
      endDate: formData.endDate,
      price: Number(formData.price),
      createPayment: formData.createPayment,
      ...(formData.createPayment && {
        paymentMethod: formData.paymentMethod || null,
        dueTime: formData.dueTime,
      }),
    };

    if (isEdit) {
      const resultAction = await dispatch(updateSubscription({ id: subscription.id, ...requestData }));

      if (updateSubscription.fulfilled.match(resultAction)) onClose();
    } else {
      const resultAction = await dispatch(createSubscription(requestData));

      if (createSubscription.fulfilled.match(resultAction)) onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleStartDateChange = useCallback(
    (direction) => handleInputChange('startDate', scrollDate(formData.startDate, direction)),
    [handleInputChange]
  );
  const handleEndDateChange = useCallback(
    (direction) => handleInputChange('endDate', scrollDate(formData.endDate, direction)),
    [handleInputChange]
  );
  const handleDueDateChange = useCallback(
    (direction) => handleInputChange('dueTime', scrollDate(formData.dueTime, direction)),
    [handleInputChange]
  );

  const calculateDuration = () => {
    if (!formData.startDate || !formData.endDate) return null;

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const diffTime = end - start;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return null;

    return `${diffDays} ${getUkrainianPlural(diffDays, ['день', 'дня', 'днів'])}`;
  };

  const handleMonthToggle = (monthDate) => {
    setSelectedMonths((prev) => {
      if (prev.includes(monthDate)) {
        return prev.filter((m) => m !== monthDate);
      }
      return [...prev, monthDate].sort();
    });
  };

  const handleSelectAllMonths = () => {
    if (selectedMonths.length === availableMonths.length) {
      setSelectedMonths([]);
    } else {
      setSelectedMonths(availableMonths.map((m) => m.date));
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.field}>
        <label className={styles.label}>
          <FontAwesomeIcon icon={faCrown} className={styles.labelIcon} />
          Тип пакету
        </label>
        <div className={styles.typeSelector}>
          {subscriptionTypes.map((type) => (
            <Button
              key={type.value}
              type='button'
              className={cn(styles.typeOption, {
                [styles.active]: formData.type === type.value,
              })}
              disabled={formData.type === type.value}
              onClick={() => handleInputChange('type', type.value)}
              noStyle
            >
              <FontAwesomeIcon icon={type.icon} />
              <span>{type.label}</span>
            </Button>
          ))}
        </div>
        {errors.type && <div className={styles.error}>{errors.type}</div>}
      </div>

      {formData.type === 'membership' && !isEdit && (
        <div className={styles.monthsSection}>
          <div className={styles.monthsHeader}>
            <label className={styles.label}>
              <FontAwesomeIcon icon={faCalendarPlus} className={styles.labelIcon} />
              Оберіть місяці ({selectedMonths.length} обрано)
            </label>
            <Button type='button' onClick={handleSelectAllMonths} ghostStyle smallStyle>
              {selectedMonths.length === availableMonths.length ? 'Скинути всі' : 'Обрати всі'}
            </Button>
          </div>

          <div className={styles.monthsGrid}>
            {availableMonths.map((month) => {
              const isSelected = selectedMonths.includes(month.date);
              return (
                <button
                  key={month.date}
                  type='button'
                  onClick={() => handleMonthToggle(month.date)}
                  className={cn(styles.monthButton, { [styles.selected]: isSelected })}
                >
                  <span className={styles.monthLabel}>{month.label}</span>
                </button>
              );
            })}
          </div>
          {errors.months && <div className={styles.error}>{errors.months}</div>}
        </div>
      )}

      {formData.type === 'subscription' && (
        <>
          <div className={styles.dateRow}>
            <div className={styles.field}>
              <span className={styles.label}>
                <FontAwesomeIcon icon={faCalendarAlt} className={styles.labelIcon} />
                Дата початку
              </span>
              <DatePicker
                className={styles.datePicker}
                calendarClassName={styles.datePickerCalendar}
                date={formData.startDate}
                handleScrollDate={handleStartDateChange}
                enableCalendar
              />
              {errors.startDate && <div className={styles.error}>{errors.startDate}</div>}
            </div>

            <div className={styles.field}>
              <span className={styles.label}>
                <FontAwesomeIcon icon={faCalendarAlt} className={styles.labelIcon} />
                Дата закінчення
              </span>
              <DatePicker
                className={styles.datePicker}
                calendarClassName={styles.datePickerCalendar}
                date={formData.endDate}
                handleScrollDate={handleEndDateChange}
                enableCalendar
              />
              {errors.endDate && <div className={styles.error}>{errors.endDate}</div>}
            </div>
          </div>

          {calculateDuration() && (
            <div className={styles.durationInfo}>
              <FontAwesomeIcon icon={faClock} />
              <span>Тривалість: {calculateDuration()}</span>
            </div>
          )}
        </>
      )}

      {!isEdit && (
        <div className={styles.paymentSection}>
          <label className={styles.toggleLabel}>
            <Checkbox
              checked={formData.createPayment}
              onChange={(e) => {
                handleInputChange('createPayment', e.target.checked);
              }}
            />
            <span>
              {formData.type === 'membership' && selectedMonths.length > 1
                ? 'Створити рахунки для оплати'
                : 'Створити рахунок для оплати'}
            </span>
          </label>

          {formData.createPayment && (
            <div className={styles.paymentFields}>
              <label className={styles.label}>
                <FontAwesomeIcon icon={faMoneyBillWave} className={styles.labelIcon} />
                Вартість (₴)
              </label>
              <Input
                type='number'
                min='1'
                step='1'
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                errorText={errors.price}
                invalid={!!errors.price}
                className={styles.input}
                placeholder='Введіть вартість'
              />

              {formData.type === 'subscription' && (
                <>
                  <label className={styles.label}>
                    <FontAwesomeIcon icon={faClock} className={styles.labelIcon} />
                    Термін оплати
                  </label>
                  <DatePicker
                    className={styles.datePicker}
                    date={formData.dueTime}
                    handleScrollDate={handleDueDateChange}
                  />
                  {errors.dueTime && <div className={styles.error}>{errors.dueTime}</div>}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.actions}>
        <Button onClick={onClose} ghostStyle>
          Скасувати
        </Button>
        <Button type='submit'>{isEdit ? 'Зберегти зміни' : 'Створити пакет'}</Button>
      </div>
    </form>
  );
};
